import { APIRoutes } from "@/api";
import { queryClient } from "@/lib/query-client";
import { ApiService } from "@/services/api/api-service";

import {
  QueryFunctionContext,
  UseQueryOptions,
  useQuery as useQueryTanstack,
} from "@tanstack/react-query";
import { z } from "zod";

const chatRoomResponseSchema = z.object({
  token: z.string(),
});

async function getRoomToken({
  queryKey,
  signal,
}: QueryFunctionContext<ReturnType<typeof ChatRoomTokenQuery.keys.room>>) {
  const roomId = queryKey[1];
  try {
    const params = new URLSearchParams({
      room: roomId,
    });

    const url = APIRoutes.CHAT_ROOM(roomId, params.toString());
    const data = await fetch(url, {
      cache: "no-cache",
      signal,
    }).then((res) => res.json());

    const response = ApiService.parseApiResponse(data, chatRoomResponseSchema);
    if (response.status === "error") {
      return Promise.reject(response.message);
    } else {
      return response.data.token;
    }
  } catch (e) {
    console.error(e);
  }
}

export namespace ChatRoomTokenQuery {
  export const keys = {
    all: ["chat-rooms"],
    room: (roomId: string) => [...keys.all, roomId, "token"] as const,
  } as const;

  export function invalidate(roomId: string) {
    return queryClient.invalidateQueries({
      queryKey: keys.room(roomId),
    });
  }

  export function setData(roomId: string, data: string) {
    return queryClient.setQueryData<string>(keys.room(roomId), data);
  }

  export function getData(roomId: string) {
    return queryClient.getQueryData<string>(keys.room(roomId));
  }

  export function useQuery(
    roomId: string,
    options?: Omit<
      UseQueryOptions<
        string | undefined,
        unknown,
        string | undefined,
        ReturnType<typeof keys.room>
      >,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQueryTanstack<
      string | undefined,
      unknown,
      string | undefined,
      ReturnType<typeof keys.room>
    >({
      queryKey: keys.room(roomId),
      queryFn: getRoomToken,
      ...options,
    });
  }
}
